import { constant } from 'lodash';
import { memo, useCallback } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';
import { type Upload } from '@air/redux-uploader';

import { type TableViewProps } from '~/components/TableView/index';
import { PrivateAssetTableRow } from '~/components/TableView/PrivateTableView/PrivateAssetTableRow';
import { TableViewItemRenamingProvider } from '~/components/TableView/PrivateTableView/TableViewItemRenamingContext';
import { SelectableTableView, type SelectableTableViewProps } from '~/components/TableView/SelectableTableView';
import { TablePageContainer } from '~/components/TableView/TablePageContainer';
import { TableViewProvider } from '~/components/TableView/TableViewContext';
import { type UsePrivateGetTableRowsParams, usePrivateTableRows } from '~/components/TableView/usePrivateTableRows';
import { useTableSelectableItems } from '~/components/TableView/useTableSelectableItems';
import { usePrivateWorkspaceAgentAssetListContext } from '~/components/v2/components/workspace/workspace-agent-asset-list/PrivateWorkspaceAgentAssetListProvider';
import { WorkspaceAgentAssetListNullState } from '~/components/v2/components/workspace/workspace-agent-asset-list/WorkspaceAgentAssetListNullState';
import { WorkspaceAgentAssetListTableHeaders } from '~/components/v2/components/workspace/workspace-agent-asset-list/WorkspaceAgentAssetListTableHeaders';
import {
  WORKSPACE_AGENT_ASSET_LIST_ACTIVE_COLUMNS,
  WORKSPACE_AGENT_ASSET_LIST_TABLE_OPTIONS,
  WORKSPACE_AGENT_ASSET_LIST_TABLE_WIDTH_SCOPE,
} from '~/components/v2/components/workspace/workspace-agent-asset-list/workspaceAgentAssetListTableColumns';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';

export type PrivateWorkspaceAgentAssetListTableViewProps = {
  scrollElementRef: SelectableTableViewProps['scrollElementRef'];
};

/** The page has no uploader: a snapshot list never gains rows from an upload. */
const NO_UPLOADS: Upload[] = [];

const isAssetSelectable = constant(true);

const renderAssetRow: NonNullable<UsePrivateGetTableRowsParams['renderAssetRow']> = ({ data, key, rowStyle }) => (
  <PrivateAssetTableRow
    configViewOptions={WORKSPACE_AGENT_ASSET_LIST_TABLE_OPTIONS}
    data={data}
    key={key}
    style={rowStyle}
  />
);

/**
 * The pieces `PrivateTableView` composes, fed by the artifact's rows instead of the search query.
 * No boards, no uploads, no search-command drop target.
 */
export const PrivateWorkspaceAgentAssetListTableView = memo(
  ({ scrollElementRef }: PrivateWorkspaceAgentAssetListTableViewProps) => {
    const { tableItemsData } = usePrivateWorkspaceAgentAssetListContext();
    const { data, hasMore, isEmpty, isLoading, isLoadingMore, loadNextPage } = tableItemsData;
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
    const { crossLocationSelectionEnabled } = useFeatureFlags();

    const rows = usePrivateTableRows({
      data,
      hasMore,
      isAssetSelectable,
      isLoading,
      isLoadingMore,
      loadMore: loadNextPage,
      renderAssetRow,
      uploads: NO_UPLOADS,
    });

    useTableSelectableItems(data, crossLocationSelectionEnabled);

    const hasData = data.length > 0;

    const renderTableHeaders: Required<TableViewProps>['renderHeaders'] = useCallback(
      (ref) =>
        (hasData || isLoading || isLoadingMore) &&
        isAboveMediumScreen && (
          <WorkspaceAgentAssetListTableHeaders horizontalPadding={responsiveHorizontalPadding} ref={ref} topPos={0} />
        ),
      [hasData, isAboveMediumScreen, isLoading, isLoadingMore, responsiveHorizontalPadding],
    );

    return (
      <TableViewProvider
        activeColumns={WORKSPACE_AGENT_ASSET_LIST_ACTIVE_COLUMNS}
        widthScope={WORKSPACE_AGENT_ASSET_LIST_TABLE_WIDTH_SCOPE}
      >
        <TableViewItemRenamingProvider>
          <TablePageContainer data-testid="WORKSPACE_AGENT_ASSET_LIST_TABLE_VIEW">
            {isEmpty ? (
              <WorkspaceAgentAssetListNullState />
            ) : (
              <SelectableTableView
                canScrollHorizontally={isAboveMediumScreen && hasData}
                horizontalPadding={responsiveHorizontalPadding}
                renderHeaders={renderTableHeaders}
                rows={rows}
                scrollElementRef={scrollElementRef}
              />
            )}
          </TablePageContainer>
        </TableViewItemRenamingProvider>
      </TableViewProvider>
    );
  },
);

PrivateWorkspaceAgentAssetListTableView.displayName = 'PrivateWorkspaceAgentAssetListTableView';
